import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { MobileShell } from '../components/MobileShell';
import { TopBar } from '../components/TopBar';
import { ChevronLeft, Phone, AlertTriangle, Lightbulb, MessageCircle, Heart, Shield, Languages } from 'lucide-react';

const EMERGENCY = [
  { name_en: 'General Emergency (Police/Fire/EMT)', name_es: 'Emergencias generales (Policía/Bomberos)', number: '911' },
  { name_en: 'Tourist Police (Quintana Roo)', name_es: 'Policía Turística (Quintana Roo)', number: '+52 998 885 2277' },
  { name_en: 'Green Angels (Roadside assistance)', name_es: 'Ángeles Verdes (Asistencia carretera)', number: '078' },
  { name_en: 'Red Cross (Cruz Roja)', name_es: 'Cruz Roja', number: '065' },
  { name_en: 'Maritime Rescue (Coast guard)', name_es: 'Rescate Marítimo (Guardia Costera)', number: '+52 998 886 0150' },
  { name_en: 'US Embassy Mexico City', name_es: 'Embajada EE.UU. Ciudad de México', number: '+52 55 8526 2561' },
  { name_en: 'Canadian Embassy', name_es: 'Embajada de Canadá', number: '+52 55 5724 7900' },
  { name_en: 'UK Embassy', name_es: 'Embajada del Reino Unido', number: '+52 55 1670 3200' },
  { name_en: 'PROFECO (Consumer protection)', name_es: 'PROFECO (Atención al consumidor)', number: '55 5568 8722' },
];

const TIPS = [
  {
    title_en: 'Tipping culture', title_es: 'Cultura de propinas',
    body_en: 'In Mexico tipping is expected. 10–15% at restaurants, $1–2 USD per drink at bars, and $1–2 USD per bag for porters.',
    body_es: 'En México la propina se espera. 10–15% en restaurantes, $1–2 USD por bebida en bares, y $1–2 USD por maleta a maleteros.'
  },
  {
    title_en: 'Use bottled water', title_es: 'Usa agua embotellada',
    body_en: 'Avoid tap water. Stick to bottled or filtered water — even for brushing your teeth in many places.',
    body_es: 'Evita el agua de la llave. Toma agua embotellada o filtrada — incluso para lavarte los dientes.'
  },
  {
    title_en: 'Stick to official taxis & rideshare', title_es: 'Usa taxis oficiales y apps',
    body_en: 'In Playa, Cancún & Tulum use sitio (official) taxis or DiDi/Uber where available. Agree on the fare before getting in.',
    body_es: 'En Playa, Cancún y Tulum usa taxis de sitio o DiDi/Uber donde funcione. Pacta la tarifa antes de subir.'
  },
  {
    title_en: 'Pay in pesos', title_es: 'Paga en pesos',
    body_en: 'Pesos almost always give you a better rate than paying in USD. Carry small bills.',
    body_es: 'Pagar en pesos casi siempre te da mejor tipo de cambio que en dólares. Lleva billetes chicos.'
  },
  {
    title_en: 'Sun & sargassum', title_es: 'Sol y sargazo',
    body_en: 'UV is intense year round. Use reef-safe sunscreen. Some beaches have sargassum seasonally — check before booking yachts.',
    body_es: 'El sol pega fuerte todo el año. Usa bloqueador reef-safe. Algunas playas tienen sargazo en temporada — revisa antes de reservar yates.'
  },
  {
    title_en: 'Keep copies', title_es: 'Guarda copias',
    body_en: 'Carry a photo of your passport in your phone and leave the original in the hotel safe.',
    body_es: 'Lleva foto del pasaporte en el celular y guarda el original en la caja fuerte del hotel.'
  },
];

const SLANG = [
  { word: '¡Órale!', meaning_en: 'Wow! / Let’s go! / Right on!', meaning_es: 'Expresa sorpresa o ánimo' },
  { word: '¡Qué padre!', meaning_en: 'How cool!', meaning_es: '¡Qué genial!' },
  { word: 'No manches', meaning_en: 'No way! / Stop messing with me', meaning_es: 'Expresión de incredulidad' },
  { word: 'Chido / Chida', meaning_en: 'Cool / Great', meaning_es: 'Algo bueno o agradable' },
  { word: 'Güey', meaning_en: 'Dude (informal among friends)', meaning_es: 'Amigo, “bro”' },
  { word: 'Chela', meaning_en: 'Beer', meaning_es: 'Cerveza' },
  { word: 'La cuenta, por favor', meaning_en: 'The check, please', meaning_es: 'Pedir la cuenta' },
  { word: '¡Aguas!', meaning_en: 'Watch out!', meaning_es: '¡Cuidado!' },
  { word: 'A poco', meaning_en: 'Really? / You don’t say', meaning_es: '¿En serio?' },
  { word: 'Ahorita', meaning_en: '“Now” — but can mean in a bit, sometime, or never 😅', meaning_es: 'Puede ser ya, en un rato… o nunca' },
];

export default function ChuyHelpPage() {
  const { language } = useLanguage();

  return (
    <MobileShell>
      <TopBar
        showBrand={false}
        title={language === 'en' ? 'Chuy Help' : 'Chuy Ayuda'}
        right={
          <Link to="/" className="w-9 h-9 rounded-full flex items-center justify-center text-muted-foreground hover:bg-muted">
            <ChevronLeft size={18} />
          </Link>
        }
      />

      {/* Hero card */}
      <div className="px-4 pt-4">
        <div className="rounded-2xl bg-gradient-to-br from-caribbean to-rosa p-5 text-white relative overflow-hidden">
          <div className="absolute -right-6 -bottom-6 opacity-20">
            <Heart size={120} />
          </div>
          <div className="flex items-center gap-3 mb-2">
            <img src="/chuy.png" alt="Chuy" className="w-12 h-12 rounded-full object-cover border-2 border-white" />
            <div>
              <p className="font-heading font-bold text-lg">¡Aquí estoy, amigo!</p>
              <p className="text-xs text-white/90">
                {language === 'en'
                  ? 'Your Mexican concierge — 24/7. Tap an emergency to call.'
                  : 'Tu concierge mexicano — 24/7. Toca un número para llamar.'}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Emergency */}
      <section className="px-4 mt-5" data-testid="chuy-help-emergency">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="text-rosa" size={18} />
          <h2 className="font-heading font-bold text-base">
            {language === 'en' ? 'Emergency numbers' : 'Números de emergencia'}
          </h2>
        </div>
        <div className="space-y-2">
          {EMERGENCY.map((e) => (
            <a
              key={e.number}
              href={`tel:${e.number.replace(/\s+/g, '')}`}
              data-testid={`emergency-${e.number.replace(/\D/g, '')}`}
              className="flex items-center gap-3 bg-card border border-border rounded-2xl p-3 active:scale-[0.98] transition-transform"
            >
              <div className="w-10 h-10 rounded-full bg-rosa/10 flex items-center justify-center text-rosa">
                <Phone size={18} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm leading-tight">
                  {language === 'en' ? e.name_en : e.name_es}
                </p>
                <p className="text-xs text-muted-foreground">{e.number}</p>
              </div>
            </a>
          ))}
        </div>
      </section>

      {/* Travel tips */}
      <section className="px-4 mt-7" data-testid="chuy-help-tips">
        <div className="flex items-center gap-2 mb-2">
          <Lightbulb className="text-gold" size={18} />
          <h2 className="font-heading font-bold text-base">
            {language === 'en' ? 'Travel tips' : 'Tips de viaje'}
          </h2>
        </div>
        <div className="space-y-3">
          {TIPS.map((t, i) => (
            <div key={i} className="bg-card border border-border rounded-2xl p-3.5">
              <p className="font-semibold text-sm">{language === 'en' ? t.title_en : t.title_es}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                {language === 'en' ? t.body_en : t.body_es}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* Slang */}
      <section className="px-4 mt-7 mb-6" data-testid="chuy-help-slang">
        <div className="flex items-center gap-2 mb-2">
          <Languages className="text-caribbean" size={18} />
          <h2 className="font-heading font-bold text-base">
            {language === 'en' ? 'Mexican slang 101' : 'Slang mexicano 101'}
          </h2>
        </div>
        <div className="space-y-2">
          {SLANG.map((s, i) => (
            <div key={i} className="flex items-start gap-3 bg-card border border-border rounded-2xl p-3">
              <div className="shrink-0 w-12 h-12 rounded-xl bg-caribbean/10 flex items-center justify-center">
                <MessageCircle size={20} className="text-caribbean" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm">{s.word}</p>
                <p className="text-xs text-muted-foreground">
                  {language === 'en' ? s.meaning_en : s.meaning_es}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Safety footer */}
      <div className="mx-4 mb-4 rounded-2xl border border-border bg-muted/50 p-3 flex items-start gap-2">
        <Shield className="text-caribbean shrink-0 mt-0.5" size={16} />
        <p className="text-[11px] text-muted-foreground">
          {language === 'en'
            ? 'In a real emergency dial 911 first, then text Chuy to coordinate help.'
            : 'En una emergencia real marca 911 primero, luego escríbele a Chuy para coordinarte.'}
        </p>
      </div>
    </MobileShell>
  );
}
